import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import AddressSchemaForm from '../../../AddressAutocompleteSearch/AddressSchemaForm';
import AddressSchemaFormUK from './AddressSchemaFormUK';
import AddressAutocompleteSearchIE from '../../../AddressAutocompleteSearch/IE';

import { loadSchema, getCountries } from '../../../../../utils/address-schema';
import { required } from '../../../../../utils/formFieldValidators';
import { getSourceCodesToDisplay } from '../../../../../utils/donationFormSourceCodeExperiment';
import { SelectField } from '../../../../01_atoms/FormElements';
import FormattedText from '../../../../01_atoms/FormattedText';

const AddressStep = ({
  country,
  setFormValue = () => {},
  heading = '',
  text = '',
  sourceCodes = [],
  sourceCode = '',
}) => {
  const [schema, setSchema] = useState(null);

  const loadCountrySchema = (country) => {
    return loadSchema(country).then((newSchema) => {
      setSchema(newSchema);
    });
  };

  /**
   * Updates the form state and therefore the appropriate inputs, with a
   * programmatically-set address, which the autocomplete & lookup utilities of
   * AddressAutofillFormUK facilitate.
   */
  const setAddressValues = (address = {}) => {
    // We need to set them individually, otherwise we overwrite other address
    // selections
    Object.keys(address).forEach((key) => setFormValue(`address.${key}`, address[key]));
  };

  const handleCountryChange = (newCountry) => {
    loadCountrySchema(newCountry.value);
  };

  useEffect(() => {
    const addressFieldsForReset = {
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      locality: '',
      administrativeArea: {},
    };
    setAddressValues(addressFieldsForReset);

    // Use Autofill form for UK country by default.
    setFormValue('addressSchemaType', {
      type: 'default',
      autoSwitched: false,
    });
  }, [schema]);

  useEffect(() => {
    loadCountrySchema(country);
  }, []);

  return (
    <>
      {heading && <h2>{heading}</h2>}
      {text && <FormattedText text={text} />}

      <SelectField
        name="address.country"
        label="Country"
        options={getCountries()}
        validate={required('Please select a country')}
        isRequired
        onOptionSelect={handleCountryChange}
        autoFill={false}
      />

      {country === 'IE' && (
        <AddressAutocompleteSearchIE
          searchInfoText="Search for your address to autofill the fields below or enter it manually"
          onAddressSearch={(resp) => {
            // Preconfigure Address fields if reformattedAddress values exist.
            if (resp.body?.reformattedAddress?.length) {
              setAddressValues({
                addressLine1: resp.body.reformattedAddress[0],
                addressLine2: resp.body.reformattedAddress[1],
                locality: resp.body.reformattedAddress[2],
                postalCode: resp.body.postcode,
                administrativeArea: {
                  IE: resp.body.reformattedAddress[3] ?? '',
                },
              });
            }
          }}
        />
      )}

      {schema && country !== 'GB' && (
        <AddressSchemaForm country={country} schema={schema} name="address" />
      )}

      {/* UK addresses will use a specific bespoke form that autofills and validates UK postcodes
        and addresses via a series of 3rd party APIs */}
      {country === 'GB' && (
        <AddressSchemaFormUK setAddressValues={setAddressValues} setFormValue={setFormValue} />
      )}

      {/* Don't show the field if the value is already populated. */}
      {sourceCodes && !sourceCode && (
        <div className="donation-form-source-codes-area">
          <SelectField
            name="source_code"
            label="What motivated your contribution today?"
            info="Your answer will help us to more effectively plan our fundraising activities"
            placeholder="Select a reason"
            menuPlacement="top"
            validate={required('Please select a reason in order to proceed')}
            isRequired
            options={getSourceCodesToDisplay(sourceCodes)}
            menuShouldScrollIntoView
            isSearchable={false}
            // We set 1000px to be sure that scrolling
            // will be appeared only if we will have more than ~7 source codes.
            styles={{ menuList: (base) => ({ ...base, maxHeight: '350px' }) }}
          />
        </div>
      )}
    </>
  );
};

AddressStep.propTypes = {
  heading: PropTypes.string,
  text: PropTypes.string,
  country: PropTypes.string.isRequired,
  sourceCodes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  setFormValue: PropTypes.func,
  sourceCode: PropTypes.string,
};

export default AddressStep;
